import type { ListingsAPIAndFilter, ListingsAPIOrFilter } from '@noths/polaris-client-listings-api';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ChangedFacetDetails } from 'src/components/organisms/ListingsControlsOptions/types';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { ListingsAPIServingConfig } from 'src/services/listings-api/servingConfigs';
import { applyFacetOptionChangeToFacets } from './helpers/facetOptionHelpers';
import type { Facet } from './types/Facet';
import type { SortOption } from './types/SortOption';

export interface ProductsQueryState {
  canonicalFilters?: ListingsAPIAndFilter | ListingsAPIOrFilter;
  facets: Facet[];
  isModalOpen: boolean;
  page: number;
  pageCategories?: string[];
  searchTerm?: string;
  servingConfig?: ListingsAPIServingConfig;
  sortOptions: SortOption[];
}

export const initialState: Readonly<ProductsQueryState> = {
  facets: [],
  isModalOpen: false,
  page: 1,
  sortOptions: [],
};

export const { actions, reducer } = createSlice({
  name: 'productsQuery',
  initialState,
  reducers: {
    closeModal: (state) => {
      state.isModalOpen = false;
    },
    openModal: (state) => {
      state.isModalOpen = true;
    },
    changeFacetOption: (state, action: PayloadAction<ChangedFacetDetails>) => {
      state.page = 1;
      state.facets = applyFacetOptionChangeToFacets(state.facets, action.payload);
    },
    resetFacets: (state) => {
      state.page = 1;
      state.facets = state.facets.map((facet) => {
        const hasActiveOption = facet.options.some((option) => option.active);

        if (hasActiveOption) {
          return {
            ...facet,
            options: facet.options
              .map((option) => {
                if (option.active) {
                  return {
                    ...option,
                    active: false,
                  };
                }

                return option;
              })
              .filter((option) => option.custom !== true),
          };
        }
        return facet;
      });
    },
    changeSortOption: (state, action: PayloadAction<string>) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        if (!action.payload) {
          return {
            ...option,
            active: false,
          };
        }
        const [key, direction] = action.payload.split('-');
        const isDescending = direction === 'desc';

        return {
          ...option,
          active: option.key === key && option.descending === isDescending,
        };
      });
    },
    resetSortOptions: (state: ProductsQueryState) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        return {
          ...option,
          active: option.key === '',
        };
      });
    },
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(requestProducts.fulfilled, (state, { payload }) => {
      if (payload.sortOptions) {
        state.sortOptions = payload.sortOptions;
      }

      if (payload.facets) {
        state.facets = payload.facets;
      }
    });
  },
});
